import { useState, lazy, Suspense } from "react";
import { Switch, Route, Router as WouterRouter } from "wouter";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { SmoothScroll } from "@/components/SmoothScroll";
import { ChatBot } from "@/components/ChatBot";
import { MobileBottomNav } from "@/components/MobileBottomNav";
import { PageTransition } from "@/components/PageTransition";
//import { BackToTop } from "@/components/BackToTop";
import { AuthProvider } from "@/context/AuthContext";
import { ShortlistProvider } from "@/context/ShortlistContext";
import { ComparisonProvider } from "@/context/ComparisonContext";
import { NotificationProvider } from "@/context/NotificationContext";
import { ThemeProvider } from "@/context/ThemeContext";
import { OnboardingGate } from "@/components/OnboardingGate";

import Home from "@/pages/Home";

const Vendors          = lazy(() => import("@/pages/Vendors"));
const Venues           = lazy(() => import("@/pages/Venues"));
const Login            = lazy(() => import("@/pages/Login"));
const Weddings         = lazy(() => import("@/pages/Weddings"));
const ListYourBusiness = lazy(() => import("@/pages/ListYourBusiness"));
const WhyChooseUs      = lazy(() => import("@/pages/WhyChooseUs"));
const PrivacyPolicy    = lazy(() => import("@/pages/PrivacyPolicy"));
const TermsOfService   = lazy(() => import("@/pages/TermsOfService"));
const CookiePolicy     = lazy(() => import("@/pages/CookiePolicy"));
const Support          = lazy(() => import("@/pages/Support"));
const RefundPolicy     = lazy(() => import("@/pages/RefundPolicy"));
const CityLanding      = lazy(() => import("@/pages/CityLanding"));
const WeddingPlanners     = lazy(() => import("@/pages/WeddingPlanners"));
const Photographers       = lazy(() => import("@/pages/Photographers"));
const CaseStudy           = lazy(() => import("@/pages/CaseStudy"));
const VendorPortalDemo    = lazy(() => import("@/pages/demos/VendorPortalDemo"));
const BookingPaymentDemo  = lazy(() => import("@/pages/demos/BookingPaymentDemo"));
const NotFound            = lazy(() => import("@/pages/not-found"));
const AdminPortal      = lazy(() => import("@/pages/portal/AdminPortal"));
const VendorPortal     = lazy(() => import("@/pages/portal/VendorPortal"));
const VenuePortal      = lazy(() => import("@/pages/portal/VenuePortal"));
const Profile          = lazy(() => import("@/pages/portal/Profile"));
const SavedFavorites   = lazy(() => import("@/pages/portal/SavedFavorites"));
const Checklist          = lazy(() => import("@/pages/Checklist"));
const GuestList          = lazy(() => import("@/pages/GuestList"));
const BudgetCalculator   = lazy(() => import("@/pages/BudgetCalculator"));
const HashtagGenerator   = lazy(() => import("@/pages/HashtagGenerator"));
const About              = lazy(() => import("@/pages/About"));
const Contact            = lazy(() => import("@/pages/Contact"));
const News               = lazy(() => import("@/pages/News"));
const Careers            = lazy(() => import("@/pages/Careers"));
const WriteAReview       = lazy(() => import("@/pages/WriteAReview"));

function PageLoader() {
  return <div className="fixed inset-0 bg-[#080604]" />;
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000,
      gcTime: 30 * 60 * 1000,
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

function Router() {
  return (
    <Switch>
      <Route path="/" component={Home} />
      <Route path="/vendors" component={Vendors} />
      <Route path="/venues" component={Venues} />
      <Route path="/login" component={Login} />
      <Route path="/weddings" component={Weddings} />
      <Route path="/real-weddings" component={Weddings} />
      <Route path="/list-your-business" component={ListYourBusiness} />
      <Route path="/why-choose-us" component={WhyChooseUs} />
      <Route path="/privacy-policy" component={PrivacyPolicy} />
      <Route path="/terms-of-service" component={TermsOfService} />
      <Route path="/cookie-policy" component={CookiePolicy} />
      <Route path="/contact-us" component={Contact} />
      <Route path="/support" component={Support} />
      <Route path="/contact" component={Contact} />
      <Route path="/refund-policy" component={RefundPolicy} />
      <Route path="/portal/admin" component={AdminPortal} />
      <Route path="/portal/vendor" component={VendorPortal} />
      <Route path="/portal/venue" component={VenuePortal} />
      <Route path="/portal/profile" component={Profile} />
      <Route path="/portal/saved" component={SavedFavorites} />
      <Route path="/wedding-planners" component={WeddingPlanners} />
      <Route path="/photographers" component={Photographers} />
      <Route path="/events/:slug" component={CaseStudy} />
      <Route path="/checklist" component={Checklist} />
      <Route path="/guest-list" component={GuestList} />
      <Route path="/budget-calculator" component={BudgetCalculator} />
      <Route path="/hashtag-generator" component={HashtagGenerator} />
      <Route path="/about" component={About} />
      <Route path="/news" component={News} />
      <Route path="/careers" component={Careers} />
      <Route path="/vendors/:city" component={CityLanding} />
      <Route path="/Feedback" component={WriteAReview} />
      <Route path="/demo/vendor-portal" component={VendorPortalDemo} />
      <Route path="/demo/booking-payment" component={BookingPaymentDemo} />
      <Route component={NotFound} />
    </Switch>
  );
}

function App() {
  return (
    <ThemeProvider>
      <QueryClientProvider client={queryClient}>
        <TooltipProvider>
          <AuthProvider>
            <NotificationProvider>
              <ComparisonProvider>
                <ShortlistProvider>
                  <SmoothScroll>
                    <WouterRouter base={import.meta.env.BASE_URL.replace(/\/$/, "")}>
                      <OnboardingGate>
                        <PageTransition />
                        <Suspense fallback={<PageLoader />}>
                          <Router />
                        </Suspense>
                        <ChatBot />
                        {/* <BackToTop /> */}
                        <MobileBottomNav />
                      </OnboardingGate>
                    </WouterRouter>
                  </SmoothScroll>
                </ShortlistProvider>
              </ComparisonProvider>
            </NotificationProvider>
          </AuthProvider>
          <Toaster />
        </TooltipProvider>
      </QueryClientProvider>
    </ThemeProvider>
  );
}

export default App;
